import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Minus, Plus, Star, ChevronLeft } from "lucide-react";
import api, { formatNaira } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { has, toggle } = useWishlist();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
  }, [id]);

  if (product === null) return <div className="max-w-7xl mx-auto p-12">Loading…</div>;
  if (product === false) return <div className="max-w-7xl mx-auto p-12">Product not found.</div>;

  const wishlisted = has(product.id);
  const discount = product.original_price ? Math.round(((product.original_price - product.price) / product.original_price) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-[#6A747D] mb-8 hover:text-[#333E48]" data-testid="back-btn">
        <ChevronLeft size={16} /> Back
      </button>

      <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
        <div className="bg-[#F3EFEA] aspect-square overflow-hidden">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
        </div>

        <div>
          <p className="label-eyebrow mb-3">{(product.categories || []).join(" · ")}</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="product-detail-name">{product.name}</h1>
          {product.rating > 0 && (
            <div className="flex items-center gap-2 mt-4 text-sm text-[#6A747D]">
              <Star size={14} fill="#FED700" color="#FED700" />
              <span>{product.rating.toFixed(2)}</span>
              <span>({product.review_count} reviews)</span>
            </div>
          )}

          <div className="flex items-baseline gap-4 mt-8">
            <span className="font-serif text-3xl" data-testid="product-detail-price">{formatNaira(product.price)}</span>
            {product.original_price && (
              <>
                <span className="text-lg text-[#9CA3AF] line-through">{formatNaira(product.original_price)}</span>
                <span className="bg-[#333E48] text-white text-xs px-2 py-1 uppercase tracking-wider">−{discount}%</span>
              </>
            )}
          </div>

          <p className="mt-8 text-[#6A747D] leading-relaxed">{product.description}</p>

          {product.features?.length > 0 && (
            <ul className="mt-6 space-y-2 text-sm">
              {product.features.map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <span className="w-1 h-1 bg-[#FED700]" />
                  {f}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-10 flex items-center gap-6">
            <div className="flex items-center border border-[#E8E2D9]" data-testid="qty-control">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3 hover:bg-[#F3EFEA]" data-testid="qty-decrease"><Minus size={14} /></button>
              <span className="px-5 font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3 py-3 hover:bg-[#F3EFEA]" data-testid="qty-increase"><Plus size={14} /></button>
            </div>
            <p className="text-xs text-[#6A747D]">SKU: {product.sku}</p>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => addToCart(product, qty)}
              className="btn-gold"
              data-testid="add-to-cart-detail-btn"
            >
              Add to Cart
            </button>
            <button
              onClick={() => { addToCart(product, qty); navigate("/cart"); }}
              className="btn-outline"
              data-testid="buy-now-btn"
            >
              Buy Now
            </button>
            <button
              onClick={() => toggle(product.id)}
              className={`w-12 h-12 flex items-center justify-center border ${wishlisted ? "bg-[#333E48] text-white border-[#333E48]" : "border-[#E8E2D9]"}`}
              data-testid="detail-wishlist-btn"
              aria-label="Wishlist"
            >
              <Heart size={16} fill={wishlisted ? "#FED700" : "none"} color={wishlisted ? "#FED700" : "currentColor"} />
            </button>
          </div>

          <div className="mt-10 pt-8 border-t border-[#E8E2D9] text-sm text-[#6A747D] space-y-2">
            <p>✦ Free shipping on orders over ₦5,000</p>
            <p>✦ Authentic handmade Adire</p>
            <p>✦ Secure checkout via Paystack</p>
          </div>
        </div>
      </div>
    </div>
  );
}
